import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { alertsAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Plus,
  Bell,
  Trash2,
  Pencil,
  Play,
  Pause,
  TestTube,
  Mail,
  MessageSquare,
  Smartphone,
  History,
  AlertTriangle,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatDistanceToNow } from 'date-fns';

const ENTITY_TYPES = [
  { value: 'campaign', label: 'Campaign' },
  { value: 'platform', label: 'Platform' },
  { value: 'advertiser', label: 'Advertiser' },
];

const FIELDS = [
  { value: 'spend', label: 'Spend ($)' },
  { value: 'impressions', label: 'Impressions' },
  { value: 'clicks', label: 'Clicks' },
  { value: 'conversions', label: 'Conversions' },
  { value: 'ctr', label: 'CTR (%)' },
  { value: 'cpc', label: 'CPC ($)' },
  { value: 'budget_used_percent', label: 'Budget Used (%)' },
];

const OPERATORS = [
  { value: 'gt', label: 'Greater than' },
  { value: 'lt', label: 'Less than' },
  { value: 'gte', label: 'Greater than or equal' },
  { value: 'lte', label: 'Less than or equal' },
  { value: 'eq', label: 'Equal to' },
  { value: 'change_percent', label: 'Changed by %' },
];

const CHANNELS = [
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'in_app', label: 'In-App', icon: Smartphone },
  { value: 'slack', label: 'Slack/Webhook', icon: MessageSquare },
];

const AlertRules = () => {
  const [activeTab, setActiveTab] = useState('rules');
  const [rules, setRules] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [testing, setTesting] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    entity_type: 'campaign',
    entity_id: '',
    conditions: [{ field: 'spend', operator: 'gt', value: 0 }],
    logic: 'and',
    channels: ['in_app'],
    slack_webhook_url: '',
    recipients: '',
    cooldown_hours: 6,
    is_active: true,
  });

  const fetchRules = async () => {
    setLoading(true);
    try {
      const response = await alertsAPI.getRules();
      setRules(response.data || []);
    } catch (error) {
      toast.error('Failed to load alert rules');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await alertsAPI.getHistory();
      setHistory(response.data || []);
    } catch (error) {
      console.error('Failed to load alert history');
    }
  };

  useEffect(() => {
    fetchRules();
    fetchHistory();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error('Rule name is required');
      return;
    }

    if (formData.conditions.length === 0) {
      toast.error('At least one condition is required');
      return;
    }

    try {
      const data = {
        ...formData,
        recipients: formData.recipients ? formData.recipients.split(',').map(e => e.trim()) : null,
        entity_id: formData.entity_id || null,
      };

      if (editingRule) {
        await alertsAPI.updateRule(editingRule.id, data);
        toast.success('Alert rule updated');
      } else {
        await alertsAPI.createRule(data);
        toast.success('Alert rule created');
      }

      setDialogOpen(false);
      resetForm();
      fetchRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save alert rule');
    }
  };

  const handleDelete = async (ruleId) => {
    if (!confirm('Are you sure you want to delete this alert rule?')) return;

    try {
      await alertsAPI.deleteRule(ruleId);
      toast.success('Alert rule deleted');
      fetchRules();
    } catch (error) {
      toast.error('Failed to delete alert rule');
    }
  };

  const handleTest = async (ruleId) => {
    setTesting(ruleId);
    try {
      const response = await alertsAPI.testRule(ruleId);
      toast.success('Test notification sent');
      console.log('Test results:', response.data);
    } catch (error) {
      toast.error('Failed to send test notification');
    } finally {
      setTesting(null);
    }
  };

  const toggleActive = async (rule) => {
    try {
      await alertsAPI.updateRule(rule.id, { is_active: !rule.is_active });
      toast.success(rule.is_active ? 'Alert rule paused' : 'Alert rule activated');
      fetchRules();
    } catch (error) {
      toast.error('Failed to update alert rule');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      entity_type: 'campaign',
      entity_id: '',
      conditions: [{ field: 'spend', operator: 'gt', value: 0 }],
      logic: 'and',
      channels: ['in_app'],
      slack_webhook_url: '',
      recipients: '',
      cooldown_hours: 6,
      is_active: true,
    });
    setEditingRule(null);
  };

  const openEditDialog = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description || '',
      entity_type: rule.entity_type,
      entity_id: rule.entity_id || '',
      conditions: rule.conditions || [{ field: 'spend', operator: 'gt', value: 0 }],
      logic: rule.logic || 'and',
      channels: rule.channels || ['in_app'],
      slack_webhook_url: rule.slack_webhook_url || '',
      recipients: rule.recipients ? rule.recipients.join(', ') : '',
      cooldown_hours: rule.cooldown_hours || 6,
      is_active: rule.is_active,
    });
    setDialogOpen(true);
  };

  const addCondition = () => {
    setFormData(prev => ({
      ...prev,
      conditions: [...prev.conditions, { field: 'spend', operator: 'gt', value: 0 }]
    }));
  };

  const removeCondition = (index) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };

  const updateCondition = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.map((c, i) =>
        i === index ? { ...c, [field]: value } : c
      )
    }));
  };

  const toggleChannel = (channel) => {
    setFormData(prev => ({
      ...prev,
      channels: prev.channels.includes(channel)
        ? prev.channels.filter(c => c !== channel)
        : [...prev.channels, channel]
    }));
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fadeIn">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900">Performance Alerts</h1>
            <p className="text-sm text-slate-500 mt-1">
              Set up automated alerts for campaign performance thresholds
            </p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="create-alert-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Alert Rule
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingRule ? 'Edit Alert Rule' : 'Create Alert Rule'}
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <div>
                    <Label>Rule Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., High Spend Alert"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Optional description"
                      rows={2}
                    />
                  </div>
                </div>

                {/* Entity Selection */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Entity Type</Label>
                    <Select
                      value={formData.entity_type}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, entity_type: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ENTITY_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Specific Entity ID (Optional)</Label>
                    <Input
                      value={formData.entity_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, entity_id: e.target.value }))}
                      placeholder="Leave empty for all"
                    />
                  </div>
                </div>

                {/* Conditions */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Conditions</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addCondition}>
                      <Plus className="w-3 h-3 mr-1" /> Add Condition
                    </Button>
                  </div>

                  <div className="space-y-2">
                    {formData.conditions.map((condition, index) => (
                      <div key={index} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                        <Select
                          value={condition.field}
                          onValueChange={(value) => updateCondition(index, 'field', value)}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {FIELDS.map(f => (
                              <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Select
                          value={condition.operator}
                          onValueChange={(value) => updateCondition(index, 'operator', value)}
                        >
                          <SelectTrigger className="w-44">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {OPERATORS.map(op => (
                              <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Input
                          type="number"
                          value={condition.value}
                          onChange={(e) => updateCondition(index, 'value', parseFloat(e.target.value) || 0)}
                          className="w-24"
                        />

                        {formData.conditions.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeCondition(index)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>

                  {formData.conditions.length > 1 && (
                    <div className="mt-2">
                      <Label className="text-sm">Condition Logic</Label>
                      <Select
                        value={formData.logic}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, logic: value }))}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="and">ALL (AND)</SelectItem>
                          <SelectItem value="or">ANY (OR)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Notification Channels */}
                <div>
                  <Label className="mb-2 block">Notification Channels</Label>
                  <div className="flex gap-4">
                    {CHANNELS.map(channel => (
                      <label
                        key={channel.value}
                        className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                          formData.channels.includes(channel.value)
                            ? 'border-slate-900 bg-slate-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Checkbox
                          checked={formData.channels.includes(channel.value)}
                          onCheckedChange={() => toggleChannel(channel.value)}
                        />
                        <channel.icon className="w-4 h-4" />
                        <span>{channel.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Channel-specific settings */}
                {formData.channels.includes('email') && (
                  <div>
                    <Label>Email Recipients (comma-separated)</Label>
                    <Input
                      value={formData.recipients}
                      onChange={(e) => setFormData(prev => ({ ...prev, recipients: e.target.value }))}
                      placeholder="email1@example.com, email2@example.com"
                    />
                  </div>
                )}

                {formData.channels.includes('slack') && (
                  <div>
                    <Label>Slack Webhook URL</Label>
                    <Input
                      value={formData.slack_webhook_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, slack_webhook_url: e.target.value }))}
                      placeholder="https://hooks.slack.com/services/..."
                    />
                  </div>
                )}

                {/* Cooldown */}
                <div>
                  <Label>Cooldown Period (hours)</Label>
                  <Input
                    type="number"
                    value={formData.cooldown_hours}
                    onChange={(e) => setFormData(prev => ({ ...prev, cooldown_hours: parseInt(e.target.value) || 6 }))}
                    className="w-24"
                    min={1}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Minimum time between repeated alerts for the same rule
                  </p>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingRule ? 'Update' : 'Create'} Rule
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="rules">Alert Rules</TabsTrigger>
            <TabsTrigger value="history">Alert History</TabsTrigger>
          </TabsList>

          <TabsContent value="rules" className="mt-4">
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : rules.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No alert rules configured</p>
                    <p className="text-sm mt-1">Create your first alert rule to get started</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {rules.map((rule) => (
                      <div key={rule.id} className="p-4 flex items-center gap-4">
                        <div className={`w-3 h-3 rounded-full ${rule.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                        
                        <div className="flex-1">
                          <div className="font-medium text-slate-900">{rule.name}</div>
                          <div className="text-sm text-slate-500">
                            {rule.entity_type} • {rule.conditions.length} condition(s) •{' '}
                            {rule.channels.join(', ')}
                          </div>
                          {rule.last_triggered && (
                            <div className="text-xs text-slate-400 mt-1">
                              Last triggered: {formatDistanceToNow(new Date(rule.last_triggered), { addSuffix: true })}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTest(rule.id)}
                            disabled={testing === rule.id}
                            title="Send test notification"
                          >
                            <TestTube className={`w-4 h-4 ${testing === rule.id ? 'animate-pulse' : ''}`} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleActive(rule)}
                            title={rule.is_active ? 'Pause' : 'Activate'}
                          >
                            {rule.is_active ? (
                              <Pause className="w-4 h-4" />
                            ) : (
                              <Play className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(rule)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(rule.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            <Card>
              <CardContent className="p-0">
                {history.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No alert history</p>
                    <p className="text-sm mt-1">Triggered alerts will appear here</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {history.map((item) => (
                      <div key={item.id} className="p-4">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                          <div className="flex-1">
                            <div className="font-medium text-slate-900">{item.rule_name}</div>
                            <div className="text-sm text-slate-500 mt-1">
                              {item.conditions_met?.join(', ')}
                            </div>
                            <div className="text-xs text-slate-400 mt-2 flex items-center gap-4">
                              <span>{formatDistanceToNow(new Date(item.triggered_at), { addSuffix: true })}</span>
                              <span>Sent via: {item.channels_sent?.join(', ')}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default AlertRules;
